import {useRef,useState} from "react";
import {useQuery,useQueryClient} from "@tanstack/react-query";
import {useAuth} from "@/contexts/AuthContext";
import {useTabDrafts} from "@/components/ui/box-draft";
import type {Result,ResultLine,TaskBox} from "@/components/ui/box.schema";
import {announcementsApi,decodeAnnouncementPost,type AnnouncementPostResult,type Audiences} from "@/lib/api/announcements";
import {ApiError} from "@/lib/api/client";
import {checkCommit,reasonOnError} from "@/lib/api/commitSeams";
import {useAnnouncementDraft,type AnnouncementDraft} from "./announcement-draft";
import {inboxRead} from "./inbox-read";
import {inboxWords as words} from "./inbox-copy";
interface PendingPost {key:string;posted?:AnnouncementPostResult}
export function useAnnouncementBox({course="",pinned=false,teaching=false,enabled}:{course?:string;pinned?:boolean;teaching?:boolean;enabled:boolean}):{box:TaskBox<Audiences>;reset():void}{
 const {me}=useAuth();const client=useQueryClient();
 const read=useQuery({queryKey:["announcements",me?.tenant_id,me?.user_id,"audiences",teaching],queryFn:()=>announcementsApi.audiences(teaching),enabled,retry:false});
 const record=`${teaching?"teaching":"institution"}/${course}`;
 const of=useTabDrafts<AnnouncementDraft,PendingPost>("new-announcement");
 const spec={record,version:null,initial:{audience:course||(read.data?.can_post_academy?"academy":""),category:"general" as const,urgent:false,title:"",body:"",email:false}};
 const keep=of(spec),value=keep.value;
 const [results,setResults]=useState<Record<string,Result>>({});
 const result=results[record]??{state:"none"};
 const store=(result:Result)=>{setResults(was=>({...was,[record]:result}));return result;};
 const running=useRef(new Map<string,Promise<Result>>());
 const sending=useRef(new Set<string>());
 function exclusive(work:()=>Promise<Result>):Promise<Result>{
  const prior=running.current.get(record);if(prior)return prior;
  const next=Promise.resolve().then(work).finally(()=>running.current.delete(record));running.current.set(record,next);return next;
 }
 const refresh=()=>void client.invalidateQueries({queryKey:["announcements"]});
 const unknown=(pending:PendingPost):Result=>({state:"unknown",cap:"",requestId:pending.key,sentence:words.postUnknown,
  check:{verb:words.checkStatus,note:words.emailCheckOnly,run:()=>exclusive(()=>check(pending))}});
 async function check(pending:PendingPost):Promise<Result>{
  try{
   const answer=await checkCommit(pending.key,decodeAnnouncementPost);
   if(answer.state==="not_committed"&&!pending.posted){
    keep.setPending(null);
    return store({state:"refused",cap:words.notPosted,sentence:words.postNotCommitted,draft:"kept"});
   }
   if(!("receipt" in answer))return store(pending.posted?observed(pending.posted,pending.key):unknown(pending));
   if(answer.receipt.action!=="announcement.post"||answer.receipt.subject_ids.join(",")!=="post,email")throw new TypeError("Post Check changed command");
   return store(observed(answer.receipt.result,pending.key));
  }catch{return store(pending.posted?observed(pending.posted,pending.key):unknown(pending));}
 }
 function observed(answer:AnnouncementPostResult,key:string):Result{
  refresh();
  if(answer.state==="confirmed"){
   keep.setPending(null);
   return {state:"confirmed",at:new Date().toISOString(),effects:answer.succeeded.map(line=>line.reason.message),remains:[]};
  }
  const pending={key,posted:answer};keep.setPending(pending);
  const email=answer.email;
  const uncertain=email.counts.uncertain>0||email.counts.sending>0||email.state==="pending"||email.state==="unknown";
  const retry=email.retry_eligible?{verb:words.emailRetry,note:words.emailRetryOnly,run:(original:string)=>exclusive(async()=>{
   if(original!==key)return store(observed(answer,key));
   try{return store(observed(await announcementsApi.retryEmail(answer.announcement_id,key,email.updated_at),key));}
   catch{return check(pending);}
  })}:undefined;
  const went=answer.succeeded.map(line=>({sentence:line.reason.message,ids:line.reason.ids}));
  const failed=email.retry_eligible?answer.failed.map(line=>({sentence:line.reason.message,ids:line.reason.ids})):[];
  const base={state:"partial" as const,at:new Date().toISOString(),requestId:key,went,failed};
  if(uncertain||!retry){
   const unsure:[ResultLine,...ResultLine[]]=[{ids:["email"],sentence:words.emailNotConfirmed(email.counts.uncertain+email.counts.sending)}];
   return {...base,unsure,check:{verb:words.checkStatus,note:words.emailCheckOnly,run:()=>exclusive(()=>check(pending))},...(retry?{retry}:{})};
  }
  return {...base,retry};
 }
 const drawn=result.state==="none"&&keep.pending&&!sending.current.has(record)
  ?keep.pending.posted?observedReadOnly(keep.pending):unknown(keep.pending):result;
 // Restoring a settled partial is a read of the tab's last evidence. It must
 // not invalidate a query or write the tab while rendering.
 function observedReadOnly(pending:PendingPost):Result{
  const answer=pending.posted!;const email=answer.email;
  const base={state:"partial" as const,at:email.updated_at,requestId:pending.key,
   went:answer.succeeded.map(line=>({sentence:line.reason.message,ids:line.reason.ids})),
   failed:email.retry_eligible?answer.failed.map(line=>({sentence:line.reason.message,ids:line.reason.ids})):[]};
  const retry=email.retry_eligible?{verb:words.emailRetry,note:words.emailRetryOnly,run:()=>exclusive(async()=>{
   try{return store(observed(await announcementsApi.retryEmail(answer.announcement_id,pending.key,email.updated_at),pending.key));}
   catch{return check(pending);}
  })}:undefined;
  if(email.counts.uncertain||email.counts.sending||email.state==="pending"||email.state==="unknown"||!retry)
   return {...base,unsure:[{ids:["email"],sentence:words.emailNotConfirmed(email.counts.uncertain+email.counts.sending)}],
    check:{verb:words.checkStatus,note:words.emailCheckOnly,run:()=>exclusive(()=>check(pending))},...(retry?{retry}:{})};
  return {...base,retry};
 }
 const change=(patch:Partial<AnnouncementDraft>)=>{if(!keep.pending){keep.set(was=>({...was,...patch}));store({state:"none"});}};
 const fields=useAnnouncementDraft(keep,read.data,change,pinned?course:undefined);
 const box:TaskBox<Audiences>={id:"new-announcement",kind:"task",label:words.newAnnouncement,job:words.prepareNotice,owner:{system:"imam"},fold:"choice",
  read:inboxRead(read,words.audiencesFailed),draft:keep.kept,result:drawn,
  state:()=>drawn.state==="partial"?{class:"reading",word:words.partlyDone}:drawn.state==="unknown"?{class:"reading",word:words.postNotConfirmed}
    :{class:drawn.state==="confirmed"?"done":keep.kept?"draft":"quiet",word:drawn.state==="confirmed"?words.noticePosted:keep.kept?words.noticeDraft:words.notStarted},
  detail:()=>pinned?words.prepareCourseNotice:words.prepareNotice,act:{does:"open",verb:words.newAnnouncement},parts:fields.parts,
  exits:drawn.state==="confirmed"?[{label:words.postAnother,reopens:true}]:undefined,
  commit:{verb:words.post,cap:words.whenPost,blockers:fields.blockers,effects:fields.effects,run:key=>exclusive(async()=>{
   const pending=of(spec).pending;if(pending)return store(pending.posted?observedReadOnly(pending):unknown(pending));
   const blocker=fields.blockers()[0];
   if(blocker&&"sentence" in blocker)return store({state:"refused",cap:words.notPosted,sentence:blocker.sentence,draft:"kept"});
   keep.setPending({key});sending.current.add(record);
   const audience=pinned?course:value.audience;
   try{return store(observed(await announcementsApi.createKeyed({scope:audience==="academy"?"academy":"course",course_id:audience==="academy"?null:audience,
    category:value.category,is_urgent:value.urgent,title:value.title.trim(),body:value.body.trim(),email_on_post:value.email},key),key));}
   catch(error){
    if(error instanceof ApiError&&error.status>=400&&error.status<500&&error.status!==408){keep.setPending(null);return store({state:"refused",cap:words.notPosted,sentence:reasonOnError(error)?.message??error.message,draft:"kept"});}
    return store(unknown({key}));
   }finally{sending.current.delete(record);}
  })},
 };
 return {box,reset:()=>{if(!keep.pending)store({state:"none"});}};
}
